import { MoonStars, SunDim, CircleHalf, Minus, Plus, Timer } from '@phosphor-icons/react';
import { TIMEOUT_OPTIONS } from '../../utils/constants.js';
import { formatTimeout, formatPrice } from '../../utils/format.js';
import { Ticker, type TickerItem } from '../shared/Ticker.js';
import { useRates } from '../../hooks/useRates.js';

const THEME_ICONS = {
  dark: MoonStars,
  classic: SunDim,
  oled: CircleHalf,
} as const;

const THEME_TITLES = {
  dark: 'Тема: Dark',
  classic: 'Тема: Classic',
  oled: 'Тема: OLED',
} as const;

interface TopStripProps {
  theme: string;
  onThemeToggle: () => void;
  timeoutIndex: number;
  onTimeoutChange: (idx: number) => void;
  /** детокс: скрыть бегущую строку котировок */
  hideTicker?: boolean;
}

export function TopStrip({ theme, onThemeToggle, timeoutIndex, onTimeoutChange, hideTicker }: TopStripProps) {
  const minutes = TIMEOUT_OPTIONS[timeoutIndex];
  const ThemeIcon = THEME_ICONS[theme as keyof typeof THEME_ICONS] ?? MoonStars;
  const themeTitle = THEME_TITLES[theme as keyof typeof THEME_ICONS] ?? 'Переключить тему';

  const rates = useRates();
  const tickerItems: TickerItem[] = rates.map((coin) => {
    const change = coin.price_change_percentage_24h ?? 0;
    const sign = change >= 0 ? '+' : '−';
    return {
      icon: coin.image ? <img src={coin.image} alt="" loading="eager" decoding="async" /> : undefined,
      label: coin.symbol.toUpperCase(),
      value: `$${formatPrice(coin.current_price)} ${sign}${Math.abs(change).toFixed(1)}%`,
      tone: change >= 0 ? 'pos' : 'neg',
    };
  });

  return (
    <header class="top-strip">
      <div class="top-strip-inner">
        <div class="masthead">
          <span class="masthead-name">
            CRYPTO<span class="em">DASH</span>
          </span>
        </div>

        {hideTicker ? (
          <div class="ticker ticker-detox" aria-label="Тикер скрыт: детокс-режим">
            <span>тикер скрыт · детокс-режим</span>
          </div>
        ) : (
          <Ticker items={tickerItems} />
        )}

        <div class="top-strip-meta">
          <div class="timeout-chip">
            <Timer size={11} weight="duotone" />
            <span class="timeout-label">Offline /</span>
            <div class="timeout-selector">
              <button
                class="timeout-btn"
                disabled={timeoutIndex <= 0}
                onClick={() => onTimeoutChange(timeoutIndex - 1)}
                type="button"
                aria-label="Уменьшить таймаут"
              >
                <Minus size={9} weight="bold" />
              </button>
              <span class="timeout-value">{formatTimeout(minutes)}</span>
              <button
                class="timeout-btn"
                disabled={timeoutIndex >= TIMEOUT_OPTIONS.length - 1}
                onClick={() => onTimeoutChange(timeoutIndex + 1)}
                type="button"
                aria-label="Увеличить таймаут"
              >
                <Plus size={9} weight="bold" />
              </button>
            </div>
          </div>

          <button
            class="btn-icon theme-toggle"
            onClick={onThemeToggle}
            title={themeTitle}
            type="button"
          >
            <ThemeIcon size={14} weight="duotone" />
          </button>
        </div>
      </div>
    </header>
  );
}
